/**
 * Admin tabs (Phase 2d; canvas AdminScreen): Role Templates, Role Access, Item & Task Assignments at #/admin/<tab>.
 * Super Admin or HR only: the header shows the entry for them, this view shows a "no access" notice to anyone else,
 * and the server is the authority (every admin endpoint is 403 for everyone else).
 *
 * Role Access is one row per person (ADR 048), never deletes a row (ADR 023: un-tick deactivates, re-tick reactivates
 * the same row), and keeps the canvas's three-tier legend verbatim. Templates and assignments keep a hard delete
 * behind a confirm. Validation is the API's own module (../../api/src/requests/validation), run here before the
 * call and again, authoritatively, on the server; a 400 is mapped onto its fields, a 409 is a notice on the form,
 * a 403 the page-level notice. Nothing here provisions anything; the only Graph read is the people picker's search.
 */
import { html, nothing, type TemplateResult } from 'lit-html';
import {
  ApiError,
  createAssignment,
  createRoleTemplate,
  deactivatePerson,
  deleteAssignment,
  deleteRoleTemplate,
  getAssignments,
  getDepartments,
  getPicker,
  getRoleAccess,
  getRoleTemplates,
  reactivatePerson,
  savePerson,
  updateAssignment,
  updateRoleTemplate,
  type Assignment,
  type Person,
  type PickerRow,
  type RoleAccessRow,
  type RoleTemplate,
} from './api';
import type { App } from './app';
import {
  checked,
  field,
  invalid,
  multiPicker,
  newMultiPick,
  newPeople,
  peoplePicker,
  select,
  switchRow,
  val,
  type FormCtx,
  type MultiPick,
  type PeoplePicker,
} from './fields';
import {
  ACCESS_ROLE_VALUES,
  ADMIN_MESSAGES,
  SOFTWARE_ITEMS,
  TASK_CATEGORIES,
  TASK_ROLE_VALUES,
  validateAssignment,
  validatePerson,
  validateTemplate,
  type AssignmentDraft,
  type FieldError,
  type PersonDraft,
  type TemplateDraft,
} from '../../api/src/requests/validation';

export type AdminTab = 'templates' | 'access' | 'assignments';
const TABS: [AdminTab, string][] = [
  ['templates', 'Role Templates'],
  ['access', 'Role Access'],
  ['assignments', 'Item & Task Assignments'],
];
export const isAdminTab = (x: unknown): x is AdminTab => TABS.some(([t]) => t === x);

/** Canvas box order (AdminScreen L1375-1813: two columns, read down then across). */
const ROLE_BOX_ORDER = ['Super Admin', 'Manager', 'IT', 'HR', 'Finance', 'Facilities', 'Business Operations'] as const;
/** Canvas "Equipment to Provision" order (L267-979) -> role_templates column. */
const EQUIPMENT_FLAGS: [string, keyof TemplateFlags][] = [
  ['Business Credit Card', 'creditCard'],
  ['Cell Phone', 'cellPhone'],
  ['Cubicle Keys', 'cubicleKeys'],
  ['Laptop', 'laptop'],
  ['Gas Pin', 'gasPin'],
  ['Key Fob', 'keyFob'],
];
interface TemplateFlags {
  creditCard: boolean;
  cellPhone: boolean;
  cubicleKeys: boolean;
  laptop: boolean;
  gasPin: boolean;
  keyFob: boolean;
}

const HELP = {
  // Canvas L266, verbatim.
  templates: 'Presets that auto-fill the onboarding form. Leave Job Title blank to create a department-wide default. Changes take effect on the next form submission.',
  // Canvas L1374 minus "Do not edit directly in SharePoint."
  access: 'Controls who can open this app and which tasks are assigned to them. Use Deactivate (not delete) to remove access.',
  // Canvas L2273 with "SharePoint column choice values" -> "the item names the request rules use"; subtitle L2282 verbatim.
  assignments:
    'Maps each task type to a role. Changes take effect on the next form submission. Item names must exactly match the item names the request rules use or tasks will silently fall back to Business Operations.',
  assignmentsSub: 'Manual tasks only — automated steps (M365, email delegation, etc.) are not listed.',
};

// ---- state ------------------------------------------------------------------------------------------------------

interface TemplateEditor {
  kind: 'template';
  id: number | null;
  title: string;
  department: string;
  jobTitle: string;
  salesforceAccess: boolean;
  mirror: PeoplePicker;
  teamsPhone: boolean;
  hasDirectReports: boolean;
  flags: TemplateFlags;
  software: Set<string>;
  sites: MultiPick;
  queues: MultiPick;
  notes: string;
  note: string;
}
interface PersonEditor {
  kind: 'person';
  isNew: boolean;
  email: string;
  userName: string;
  roles: Set<string>;
  notes: string;
  picker: PeoplePicker;
  active: boolean;
  holdsSuperAdmin: boolean;
}
interface AssignmentEditor {
  kind: 'assignment';
  id: number | null;
  title: string;
  category: string;
  taskRole: string;
  protected: boolean;
}
type Editor = (TemplateEditor | PersonEditor | AssignmentEditor) & { errors: FieldError[]; formError: string; busy: boolean };

interface Confirm {
  title: string;
  body: string;
  label: string;
  danger: boolean;
  run(): Promise<void>;
}

/** One row per person (ADR 048), from the flat role_access rows. Name and notes come from the newest row. */
interface PersonView {
  email: string;
  userName: string;
  notes: string | null;
  active: boolean;
  roles: string[];
  holdsSuperAdmin: boolean;
}

const st = {
  tab: 'templates' as AdminTab,
  ready: false,
  loading: false,
  loadError: '',
  forbidden: false,
  departments: [] as string[],
  sites: [] as PickerRow[],
  queues: [] as PickerRow[],
  templates: [] as RoleTemplate[],
  roleRows: [] as RoleAccessRow[],
  assignments: [] as Assignment[],
  legendOpen: typeof window !== 'undefined' && window.innerWidth >= 1024, // canvas: expanded on wide screens only
  editor: null as Editor | null,
  confirm: null as Confirm | null,
  notice: null as { message: string; tone: 'ok' | 'err' } | null,
  seq: 0,
};

export function enterAdmin(app: App, tab: AdminTab): void {
  st.tab = tab;
  st.editor = null;
  st.confirm = null;
  st.notice = null;
  if (!app.me.access.canAdmin) {
    st.forbidden = true;
    return;
  }
  st.forbidden = false;
  void load(app);
}

async function load(app: App): Promise<void> {
  const seq = ++st.seq;
  st.loading = true;
  st.loadError = '';
  app.redraw();
  try {
    const [departments, sites, queues, templates, roleRows, assignments] = await Promise.all([
      getDepartments(),
      getPicker('sharepoint-sites'),
      getPicker('call-queues'),
      getRoleTemplates(),
      getRoleAccess(),
      getAssignments(),
    ]);
    if (seq !== st.seq) return;
    st.departments = departments.filter((d) => d.active).map((d) => d.name);
    st.sites = sites;
    st.queues = queues;
    st.templates = templates;
    st.roleRows = roleRows;
    st.assignments = assignments;
    st.ready = true;
  } catch (e) {
    if (seq !== st.seq) return;
    if (e instanceof ApiError && e.status === 403) st.forbidden = true;
    else st.loadError = (e as Error).message || 'The admin data could not be loaded.';
  }
  st.loading = false;
  app.redraw();
}

export function people(rows: readonly RoleAccessRow[]): PersonView[] {
  const byEmail = new Map<string, RoleAccessRow[]>();
  for (const r of rows) {
    const k = r.user_email.toLowerCase();
    byEmail.set(k, [...(byEmail.get(k) ?? []), r]);
  }
  return [...byEmail.entries()]
    .map(([email, rs]) => {
      const newest = [...rs].sort((a, b) => b.id - a.id)[0];
      const active = new Set(rs.filter((r) => r.active).map((r) => r.role));
      return {
        email,
        userName: newest.user_name ?? email,
        notes: newest.notes,
        active: active.size > 0,
        roles: ACCESS_ROLE_VALUES.filter((r) => active.has(r)),
        holdsSuperAdmin: active.has('Super Admin'),
      };
    })
    .sort((a, b) => a.userName.localeCompare(b.userName) || a.email.localeCompare(b.email));
}

// ---- helpers ----------------------------------------------------------------------------------------------------

const ed = () => st.editor!;

function touch(app: App, ...fields: string[]): void {
  const e = st.editor;
  if (!e) return;
  const before = e.errors.length;
  e.errors = e.errors.filter((x) => !fields.includes(x.field));
  if (e.errors.length !== before) app.redraw();
}

const cx = (app: App): FormCtx => ({ app, errors: () => st.editor?.errors ?? [], touch: (...f) => touch(app, ...f) });

function closeEditor(app: App): void {
  st.editor = null;
  app.redraw();
}

/** Shared failure mapping: 400 -> fields, 409 -> form notice, 403 -> form notice (the page keeps working), else notice. */
function fail(app: App, e: unknown): void {
  const editor = st.editor;
  if (e instanceof ApiError && e.status === 400 && e.fieldErrors.length && editor) {
    editor.errors = e.fieldErrors;
  } else if (editor) {
    editor.formError = e instanceof ApiError ? e.message : (e as Error).message || 'The change could not be saved.';
  } else {
    st.notice = { message: e instanceof ApiError ? e.message : (e as Error).message || 'The change could not be saved.', tone: 'err' };
  }
  app.redraw();
}

async function run(app: App, work: () => Promise<string>): Promise<void> {
  const editor = st.editor;
  if (editor) {
    editor.busy = true;
    editor.formError = '';
  }
  app.redraw();
  try {
    const message = await work();
    st.editor = null;
    st.notice = { message, tone: 'ok' };
  } catch (e) {
    if (editor) editor.busy = false;
    fail(app, e);
    return;
  }
  app.redraw();
}

function textInput(app: App, id: string, label: string, value: string, set: (v: string) => void, opts: { required?: boolean; hint?: string; disabled?: boolean; type?: string; max?: number } = {}) {
  return field(
    cx(app),
    id,
    label,
    html`<input id=${id} type=${opts.type ?? 'text'} maxlength=${opts.max ?? 255} .value=${value} ?disabled=${opts.disabled ?? false}
      aria-invalid=${invalid(cx(app), id)} aria-describedby=${opts.hint ? `${id}-hint` : nothing}
      @input=${(e: Event) => {
        set(val(e));
        touch(app, id);
      }}>`,
    { required: opts.required, hint: opts.hint },
  );
}

function notesInput(app: App, value: string, set: (v: string) => void) {
  return field(
    cx(app),
    'notes',
    'Notes',
    html`<textarea id="notes" class="notes" maxlength="1000" .value=${value} aria-invalid=${invalid(cx(app), 'notes')} @input=${(e: Event) => {
      set((e.target as HTMLTextAreaElement).value);
      touch(app, 'notes');
    }}></textarea>`,
    { wide: true },
  );
}

function errorBox(editor: Editor): TemplateResult | typeof nothing {
  if (!editor.errors.length && !editor.formError) return nothing;
  return html`<div class="notice err" id="admin-errors" tabindex="-1" role="alert">
    ${editor.formError ? html`<b>${editor.formError}</b>` : html`<b>Please fix the following before saving:</b>`}
    ${editor.errors.length ? html`<ul>${editor.errors.map((e) => html`<li>${e.message}</li>`)}</ul>` : nothing}
  </div>`;
}

function confirmDialog(app: App): TemplateResult | typeof nothing {
  const c = st.confirm;
  if (!c) return nothing;
  return html`
    <div class="modal-scrim">
      <div class="modal card" role="dialog" aria-modal="true" aria-labelledby="cf-title">
        <h2 id="cf-title">${c.title}</h2>
        <p>${c.body}</p>
        <div class="modal-acts">
          <button type="button" class="btn ${c.danger ? 'danger' : ''}" @click=${async () => {
            st.confirm = null;
            await c.run();
          }}>${c.label}</button>
          <button type="button" class="btn ghost" @click=${() => {
            st.confirm = null;
            app.redraw();
          }}>Go back</button>
        </div>
      </div>
    </div>`;
}

// ---- Role Templates ---------------------------------------------------------------------------------------------

function openTemplate(app: App, t: RoleTemplate | null): void {
  const queueIds = t
    ? st.queues.filter((q) => t.call_queues.some((c) => c.address.toLowerCase() === (q.resource_account_upn ?? '').toLowerCase())).map((q) => q.id)
    : [];
  const siteIds = t ? t.sharepoint_sites.map((s) => s.site_id).filter((id) => st.sites.some((r) => r.id === id)) : [];
  const missing = t ? t.call_queues.length - queueIds.length + (t.sharepoint_sites.length - siteIds.length) : 0;
  st.editor = {
    kind: 'template',
    id: t?.id ?? null,
    title: t?.title ?? '',
    department: t?.department ?? '',
    jobTitle: t?.job_title ?? '',
    salesforceAccess: t?.salesforce_access ?? false,
    mirror: newPeople(t?.salesforce_mirror_email ? { displayName: t.salesforce_mirror_email, email: t.salesforce_mirror_email } : null),
    teamsPhone: t?.teams_phone ?? false,
    hasDirectReports: t?.has_direct_reports ?? false,
    flags: {
      creditCard: t?.credit_card ?? false,
      cellPhone: t?.cell_phone ?? false,
      cubicleKeys: t?.cubicle_keys ?? false,
      laptop: t?.laptop ?? false,
      gasPin: t?.gas_pin ?? false,
      keyFob: t?.key_fob ?? false,
    },
    software: new Set(t?.software ?? []),
    sites: newMultiPick(siteIds),
    queues: newMultiPick(queueIds),
    notes: t?.notes ?? '',
    note: missing > 0 ? `${missing} saved pick(s) are no longer in the site or call-queue lists and were dropped; save to confirm.` : '',
    errors: [],
    formError: '',
    busy: false,
  };
  app.redraw();
  requestAnimationFrame(() => document.getElementById('title')?.focus());
}

function templateDraft(e: TemplateEditor): TemplateDraft {
  return {
    title: e.title,
    department: e.department,
    jobTitle: e.jobTitle,
    salesforceAccess: e.salesforceAccess,
    salesforceMirrorEmail: e.salesforceAccess ? (e.mirror.sel?.email ?? '') : '',
    teamsPhone: e.teamsPhone,
    hasDirectReports: e.hasDirectReports,
    ...e.flags,
    notes: e.notes,
    software: [...e.software],
    callQueues: e.teamsPhone
      ? e.queues.sel.map((id) => {
          const q = st.queues.find((x) => x.id === id);
          return { label: q?.label ?? id, address: q?.resource_account_upn ?? '' };
        })
      : [],
    sharepointSiteIds: e.sites.sel,
  };
}

async function saveTemplate(app: App): Promise<void> {
  const e = ed() as TemplateEditor & Editor;
  const d = templateDraft(e);
  e.errors = validateTemplate(d, { departments: st.departments }).errors;
  if (e.errors.length) {
    app.redraw();
    requestAnimationFrame(() => document.getElementById('admin-errors')?.focus());
    return;
  }
  await run(app, async () => {
    const r = e.id == null ? await createRoleTemplate(d) : await updateRoleTemplate(e.id, d);
    st.templates = await getRoleTemplates();
    return `${e.id == null ? 'Template added' : 'Template saved'}: ${r.title}`;
  });
}

function askDeleteTemplate(app: App, e: TemplateEditor): void {
  if (e.id == null) return;
  const id = e.id;
  st.confirm = {
    title: 'Delete this template?',
    body: `Delete “${e.title}”? Requests already submitted with it are not affected.`,
    label: 'Delete',
    danger: true,
    run: () =>
      run(app, async () => {
        const r = await deleteRoleTemplate(id);
        st.templates = st.templates.filter((t) => t.id !== id);
        return `Template deleted: ${r.title}`;
      }),
  };
  app.redraw();
}

const lc = (x: string | null | undefined) => (x ?? '').toLowerCase();

function templateEditor(app: App, e: TemplateEditor & Editor): TemplateResult {
  const c = cx(app);
  const sw = (label: string, on: boolean, set: (v: boolean) => void, hint?: string) => switchRow(c, label, on, set, hint);
  return html`
    <form class="card editor ${e.id == null ? '' : 'edit'}" novalidate @submit=${(ev: Event) => {
      ev.preventDefault();
      void saveTemplate(app);
    }}>
      <h3>${e.id == null ? 'Add role template' : 'Edit role template'}</h3>
      ${errorBox(e)}
      ${e.note ? html`<p class="soft">${e.note}</p>` : nothing}
      <div class="grid2">
        ${textInput(app, 'title', 'Title', e.title, (v) => (e.title = v), { required: true })}
        ${select(c, 'department', 'Department', st.departments, e.department, (v) => (e.department = v), { required: true })}
        ${textInput(app, 'jobTitle', 'Job title', e.jobTitle, (v) => (e.jobTitle = v), { hint: 'Leave blank for a department-wide default.' })}
        ${sw('Has direct reports', e.hasDirectReports, (v) => (e.hasDirectReports = v))}
      </div>
      <div class="stack">
        ${sw('Salesforce access', e.salesforceAccess, (v) => (e.salesforceAccess = v))}
        ${e.salesforceAccess
          ? html`<div class="indent">${peoplePicker(c, 'mirror', 'Salesforce mirror user', e.mirror, 'salesforceMirrorEmail', false, 'Optional. Their Salesforce profile and permission sets are copied from this person.')}</div>`
          : nothing}
        ${sw('Teams phone', e.teamsPhone, (v) => (e.teamsPhone = v))}
        ${e.teamsPhone
          ? html`<div class="indent">
              ${multiPicker(c, 'tpl-queues', 'Call queues', st.queues, e.queues, {
                placeholder: 'Select or search call queues…',
                errField: 'callQueues',
                match: (r, q) => lc(r.label).startsWith(q) || lc(r.resource_account_upn).startsWith(q),
                detail: (r) => r.resource_account_upn ?? '',
              })}
            </div>`
          : nothing}
        ${multiPicker(c, 'tpl-sites', 'SharePoint sites', st.sites, e.sites, {
          placeholder: 'Select or search SharePoint sites…',
          errField: 'sharepointSiteIds',
          match: (r, q) => lc(r.label).includes(q) || lc(r.group_name).includes(q),
          detail: (r) => r.site_type ?? '',
        })}
      </div>
      <fieldset class="field checks ${e.errors.some((x) => x.field === 'software') ? 'has-err' : ''}">
        <legend>Software</legend>
        <div class="opts">
          ${SOFTWARE_ITEMS.map(
            (x) => html`<label class="opt"><input type="checkbox" .checked=${e.software.has(x)} @change=${(ev: Event) => {
              checked(ev) ? e.software.add(x) : e.software.delete(x);
              touch(app, 'software');
            }}> ${x}</label>`,
          )}
        </div>
      </fieldset>
      <fieldset class="field checks">
        <legend>Equipment to provision</legend>
        <div class="opts">
          ${EQUIPMENT_FLAGS.map(
            ([label, key]) => html`<label class="opt"><input type="checkbox" .checked=${e.flags[key]} @change=${(ev: Event) => {
              e.flags[key] = checked(ev);
            }}> ${label}</label>`,
          )}
        </div>
      </fieldset>
      ${notesInput(app, e.notes, (v) => (e.notes = v))}
      <div class="form-acts">
        <button class="btn" type="submit" ?disabled=${e.busy}>${e.busy ? 'Saving…' : 'Save'}</button>
        <button class="btn ghost" type="button" @click=${() => closeEditor(app)}>Cancel</button>
        ${e.id != null ? html`<button class="btn ghost danger-text" type="button" ?disabled=${e.busy} @click=${() => askDeleteTemplate(app, e)}>Delete</button>` : nothing}
      </div>
    </form>`;
}

function templatesTab(app: App): TemplateResult {
  const e = st.editor;
  return html`
    <div class="adm-head">
      <p>${HELP.templates}</p>
      <button type="button" class="btn" ?disabled=${!!e} @click=${() => openTemplate(app, null)}>
        <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg> Add
      </button>
    </div>
    ${e?.kind === 'template' ? templateEditor(app, e) : nothing}
    <div class="list">
      ${st.templates.length
        ? st.templates.map(
            (t) => html`<div class="row adm">
              <div class="cols">
                <b>${t.title}</b>
                <span>${t.department}</span>
                <span>${t.job_title || '(any job title)'}</span>
              </div>
              <button type="button" class="mini" @click=${() => openTemplate(app, t)}>Edit</button>
            </div>`,
          )
        : html`<p class="list-empty">No role templates yet.</p>`}
    </div>`;
}

// ---- Role Access ------------------------------------------------------------------------------------------------

function openPerson(app: App, p: PersonView | null): void {
  st.editor = {
    kind: 'person',
    isNew: !p,
    email: p?.email ?? '',
    userName: p?.userName ?? '',
    roles: new Set(p?.roles ?? []),
    notes: p?.notes ?? '',
    picker: newPeople(),
    active: p?.active ?? true,
    holdsSuperAdmin: p?.holdsSuperAdmin ?? false,
    errors: [],
    formError: '',
    busy: false,
  };
  app.redraw();
  requestAnimationFrame(() => document.getElementById(p ? 'userName' : 'person')?.focus());
}

function personDraft(e: PersonEditor): PersonDraft {
  return { userName: e.userName, roles: [...e.roles], notes: e.notes };
}

/** Splice the server's view of one person into the flat rows the list is built from. */
function applyPerson(p: Person): void {
  st.roleRows = [...st.roleRows.filter((r) => r.user_email.toLowerCase() !== p.email), ...p.rows];
}

async function savePersonForm(app: App): Promise<void> {
  const e = ed() as PersonEditor & Editor;
  const d = personDraft(e);
  const email = e.email.trim().toLowerCase();
  e.errors = validatePerson(email, d).errors;
  if (e.errors.length) {
    app.redraw();
    requestAnimationFrame(() => document.getElementById('admin-errors')?.focus());
    return;
  }
  await run(app, async () => {
    const p = await savePerson(email, d);
    applyPerson(p);
    return `${e.isNew ? 'Added' : 'Saved'}: ${p.userName}`;
  });
}

function askTogglePerson(app: App, e: PersonEditor, activate: boolean): void {
  const email = e.email;
  st.confirm = activate
    ? {
        title: `Reactivate ${e.userName}?`,
        body: 'Every role they held becomes active again. You can un-tick roles afterwards.',
        label: 'Reactivate',
        danger: false,
        run: () =>
          run(app, async () => {
            applyPerson(await reactivatePerson(email));
            return `Reactivated: ${e.userName}`;
          }),
      }
    : {
        title: `Deactivate ${e.userName}?`,
        body: `Deactivate ${e.userName}? They keep their history and can be reactivated.`,
        label: 'Deactivate',
        danger: true,
        run: () =>
          run(app, async () => {
            applyPerson(await deactivatePerson(email));
            return `Deactivated: ${e.userName}`;
          }),
      };
  app.redraw();
}

function personEditor(app: App, e: PersonEditor & Editor): TemplateResult {
  const c = cx(app);
  const me = app.me.access;
  const isSelf = e.email.trim().toLowerCase() === me.email.toLowerCase();
  const roleErr = e.errors.some((x) => x.field === 'roles');
  return html`
    <form class="card editor ${e.isNew ? '' : 'edit'}" novalidate @submit=${(ev: Event) => {
      ev.preventDefault();
      void savePersonForm(app);
    }}>
      <h3>${e.isNew ? 'Add person' : 'Edit person'}</h3>
      ${errorBox(e)}
      ${e.isNew
        ? peoplePicker(c, 'person', 'Find in the directory', e.picker, 'email', false, 'Fills the name and e-mail below. A mailbox the search cannot list can still be typed in.', (p) => {
            e.userName = p.displayName;
            e.email = p.email;
            touch(app, 'userName', 'email');
          })
        : nothing}
      <div class="grid2">
        ${textInput(app, 'userName', 'Display name', e.userName, (v) => (e.userName = v), { required: true })}
        ${textInput(app, 'email', 'E-mail', e.email, (v) => (e.email = v), {
          required: true,
          type: 'email',
          disabled: !e.isNew,
          hint: e.isNew ? 'Must be a mowctx.org address.' : 'To change an address, add the new one and deactivate this one.',
        })}
      </div>
      <fieldset class="field checks ${roleErr ? 'has-err' : ''}">
        <legend>Roles (select all that apply)<span class="req" aria-hidden="true"> *</span></legend>
        <div class="opts roles">
          ${ROLE_BOX_ORDER.map((role) => {
            const sa = role === 'Super Admin';
            const disabled = sa && (!me.isSuperAdmin || (isSelf && e.roles.has(role)));
            return html`<label class="opt ${disabled ? 'off' : ''}"><input type="checkbox" .checked=${e.roles.has(role)} ?disabled=${disabled} @change=${(ev: Event) => {
              checked(ev) ? e.roles.add(role) : e.roles.delete(role);
              touch(app, 'roles');
            }}> ${role}</label>`;
          })}
        </div>
        ${!me.isSuperAdmin ? html`<p class="hint">${ADMIN_MESSAGES.superAdminOnly}.</p>` : nothing}
        ${me.isSuperAdmin && isSelf && e.roles.has('Super Admin') ? html`<p class="hint">${ADMIN_MESSAGES.ownAccess}.</p>` : nothing}
        ${e.errors.filter((x) => x.field === 'roles').map((x) => html`<p class="ferr">${x.message}</p>`)}
      </fieldset>
      ${notesInput(app, e.notes, (v) => (e.notes = v))}
      <div class="form-acts">
        <button class="btn" type="submit" ?disabled=${e.busy}>${e.busy ? 'Saving…' : 'Save'}</button>
        <button class="btn ghost" type="button" @click=${() => closeEditor(app)}>Cancel</button>
        ${!e.isNew && e.active
          ? html`<button class="btn ghost danger-text" type="button" ?disabled=${e.busy || isSelf} title=${isSelf ? ADMIN_MESSAGES.ownAccess : ''}
              @click=${() => askTogglePerson(app, e, false)}>Deactivate</button>`
          : nothing}
        ${!e.isNew && !e.active ? html`<button class="btn ghost" type="button" ?disabled=${e.busy} @click=${() => askTogglePerson(app, e, true)}>Reactivate</button>` : nothing}
      </div>
    </form>`;
}

/** Canvas L1853-1917, verbatim (ADR 048's three-tier card). Collapsible; open by default on wide screens. */
function legend(app: App): TemplateResult {
  return html`
    <details class="card legend" ?open=${st.legendOpen} @toggle=${(ev: Event) => (st.legendOpen = (ev.target as HTMLDetailsElement).open)}>
      <summary>What each role means</summary>
      <p><b>SYSTEM ROLE</b> — Super Admin — Full access. Manages all settings, roles, and templates. Can view and edit any request.</p>
      <p><b>SUBMISSION ROLE</b> — Manager — Can submit onboarding and offboarding requests for their direct reports. Can view their own submissions.</p>
      <p><b>TASK ROLES</b> — IT, HR, Finance, Facilities, Business Operations — receive Planner tasks for the work items assigned to their role in the table below.</p>
      <p class="muted">A person can hold multiple roles. Select all that apply when adding someone.</p>
    </details>`;
}

function accessTab(app: App): TemplateResult {
  const e = st.editor;
  const rows = people(st.roleRows);
  return html`
    <div class="adm-head">
      <p>${HELP.access}</p>
      <button type="button" class="btn" ?disabled=${!!e} @click=${() => openPerson(app, null)}>
        <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg> Add
      </button>
    </div>
    ${e?.kind === 'person' ? personEditor(app, e) : nothing}
    ${legend(app)}
    <div class="list">
      ${rows.length
        ? rows.map(
            (p) => html`<div class="row adm ${p.active ? '' : 'inactive'}">
              <div class="cols">
                <span class="t"><b>${p.userName}</b><span>${p.email}</span></span>
                <span class="tags">${p.roles.length ? p.roles.map((r) => html`<span class="tag">${r}</span>`) : html`<span class="none">—</span>`}</span>
                <span class="pill ${p.active ? 'pill-ok' : 'pill-idle'}">${p.active ? 'Active' : 'Inactive'}</span>
              </div>
              <button type="button" class="mini" @click=${() => openPerson(app, p)}>Edit</button>
            </div>`,
          )
        : html`<p class="list-empty">Nobody has a role yet.</p>`}
    </div>`;
}

// ---- Item & Task Assignments ------------------------------------------------------------------------------------

function openAssignment(app: App, a: Assignment | null): void {
  st.editor = {
    kind: 'assignment',
    id: a?.id ?? null,
    title: a?.title ?? '',
    category: a?.category ?? '',
    taskRole: a?.task_role ?? '',
    protected: a?.protected ?? false,
    errors: [],
    formError: '',
    busy: false,
  };
  app.redraw();
  requestAnimationFrame(() => document.getElementById(a?.protected ? 'category' : 'title')?.focus());
}

async function saveAssignment(app: App): Promise<void> {
  const e = ed() as AssignmentEditor & Editor;
  const d: AssignmentDraft = { title: e.title, category: e.category, taskRole: e.taskRole };
  e.errors = validateAssignment(d).errors;
  if (e.errors.length) {
    app.redraw();
    requestAnimationFrame(() => document.getElementById('admin-errors')?.focus());
    return;
  }
  await run(app, async () => {
    const r = e.id == null ? await createAssignment(d) : await updateAssignment(e.id, d);
    st.assignments = await getAssignments();
    return `${e.id == null ? 'Item added' : 'Item saved'}: ${r.title}`;
  });
}

function askDeleteAssignment(app: App, e: AssignmentEditor): void {
  if (e.id == null) return;
  const id = e.id;
  st.confirm = {
    title: 'Delete this item?',
    body: `Delete “${e.title}”? Tasks for an item with no row fall back to Business Operations.`,
    label: 'Delete',
    danger: true,
    run: () =>
      run(app, async () => {
        const r = await deleteAssignment(id);
        st.assignments = st.assignments.filter((a) => a.id !== id);
        return `Item deleted: ${r.title}`;
      }),
  };
  app.redraw();
}

function assignmentEditor(app: App, e: AssignmentEditor & Editor): TemplateResult {
  const c = cx(app);
  return html`
    <form class="card editor ${e.id == null ? '' : 'edit'}" novalidate @submit=${(ev: Event) => {
      ev.preventDefault();
      void saveAssignment(app);
    }}>
      <h3>${e.id == null ? 'Add item & task assignment' : 'Edit item & task assignment'}</h3>
      ${errorBox(e)}
      <div class="grid2">
        ${textInput(app, 'title', 'Item name', e.title, (v) => (e.title = v), {
          required: true,
          disabled: e.protected,
          hint: e.protected ? ADMIN_MESSAGES.assignmentProtectedTitle : undefined,
        })}
        ${select(c, 'category', 'Category', TASK_CATEGORIES, e.category, (v) => (e.category = v), { required: true })}
        ${select(c, 'taskRole', 'Role', TASK_ROLE_VALUES, e.taskRole, (v) => (e.taskRole = v), { required: true, placeholder: 'Choose a role' })}
      </div>
      <div class="form-acts">
        <button class="btn" type="submit" ?disabled=${e.busy}>${e.busy ? 'Saving…' : 'Save'}</button>
        <button class="btn ghost" type="button" @click=${() => closeEditor(app)}>Cancel</button>
        ${e.id != null && !e.protected
          ? html`<button class="btn ghost danger-text" type="button" ?disabled=${e.busy} @click=${() => askDeleteAssignment(app, e)}>Delete</button>`
          : nothing}
      </div>
    </form>`;
}

function assignmentsTab(app: App): TemplateResult {
  const e = st.editor;
  return html`
    <div class="adm-head">
      <div>
        <p>${HELP.assignments}</p>
        <p class="muted">${HELP.assignmentsSub}</p>
      </div>
      <button type="button" class="btn" ?disabled=${!!e} @click=${() => openAssignment(app, null)}>
        <svg class="i" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg> Add
      </button>
    </div>
    ${e?.kind === 'assignment' ? assignmentEditor(app, e) : nothing}
    <div class="list">
      ${st.assignments.length
        ? st.assignments.map(
            (a) => html`<div class="row adm">
              <div class="cols">
                <span class="t"><b>${a.title}</b>${a.protected ? html`<span class="lock" title=${ADMIN_MESSAGES.assignmentProtectedTitle}>🔒 used by request rules</span>` : nothing}</span>
                <span><span class="tag">${a.category}</span></span>
                <span>${a.task_role}</span>
              </div>
              <button type="button" class="mini" @click=${() => openAssignment(app, a)}>Edit</button>
            </div>`,
          )
        : html`<p class="list-empty">No items yet.</p>`}
    </div>`;
}

// ---- view -------------------------------------------------------------------------------------------------------

export function adminView(app: App): TemplateResult {
  const back = html`<p><a class="back" href="#/">← Requests</a></p>`;
  if (st.forbidden) {
    return html`<main id="main" class="wrap">
      ${back}
      <div class="hello"><h1>Admin</h1></div>
      <div class="notice err" role="alert">You need the Super Admin or HR role to open Admin.</div>
    </main>`;
  }
  if (st.loadError) {
    return html`<main id="main" class="wrap">
      ${back}
      <div class="hello"><h1>Admin</h1></div>
      <div class="notice err" role="alert">${st.loadError} <button class="mini" type="button" @click=${() => load(app)}>Try again</button></div>
    </main>`;
  }
  return html`
    <main id="main" class="wrap admin-page form-page">
      ${back}
      <div class="hello"><h1>Admin</h1></div>
      <div class="toolbar">
        <nav class="tabs" aria-label="Admin sections">
          ${TABS.map(([t, label]) => html`<a class="chip" href="#/admin/${t}" aria-pressed=${st.tab === t ? 'true' : 'false'} aria-current=${st.tab === t ? 'page' : nothing}>${label}</a>`)}
        </nav>
      </div>
      ${st.notice
        ? html`<div class="toast ${st.notice.tone}" role="status">${st.notice.message}
            <button type="button" class="toast-x" aria-label="Dismiss" @click=${() => {
              st.notice = null;
              app.redraw();
            }}>×</button></div>`
        : nothing}
      ${!st.ready
        ? html`<div class="card"><p>${st.loading ? 'Loading…' : ''}</p></div>`
        : st.tab === 'templates'
          ? templatesTab(app)
          : st.tab === 'access'
            ? accessTab(app)
            : assignmentsTab(app)}
      ${confirmDialog(app)}
    </main>`;
}
